import React, { useEffect } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { makeStyles, createStyles } from "@material-ui/core/styles";
import {
  makePassivePeersSelector,
  makeSpotlightPeersSelector,
  peersSelector,
} from "../../../helpers/Selectors";
import { FormattedMessage, useIntl } from "react-intl";
import PeerListItem from "./PeerListItem";
import ListMe from "./ListMe";
import ParticipantDetail from "./ParticipantDetail";
import List from "@material-ui/core/List";
import ListSubheader from "@material-ui/core/ListSubheader";
import { Typography, IconButton, TextField } from "@material-ui/core";
import SkipPreviousIcon from "@material-ui/icons/SkipPrevious";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import NavigateBeforeIcon from "@material-ui/icons/NavigateBefore";
import SearchIcon from "@material-ui/icons/Search";
import MoreActionsMenu from "./MoreActionsMenu";

const useStyles = makeStyles((theme) =>
  createStyles({
    divider: {
      backgroundColor: theme.palette.type === "dark" ? "#3c497d" : "#f7f8fd",
      height: 8,
    },
    listHeader: {
      display: "flex",
      alignItems: "center",
      padding: 16,
    },
    listHeaderText: {
      flexGrow: "1",
      color: theme.palette.type === "dark" ? "#fff" : "#181C1E",
    },
    iconButton: {
      borderRadius: "50%",
      border: "1px solid #eaedf0",
      backgroundColor: "#ffffff",
      padding: 5,
      color: "#8790A6",
    },
    textInput: {
      height: 50,
      marginLeft: "15px",
      width: "90%",
    },
    center: {
      alignItems: "center",
      display: "flex",
      justifyContent: "center",
    },
  })
);

const ParticipantList = () => {
  const intl = useIntl();
  const classes = useStyles();
  const [limit, setLimit] = React.useState(10);
  const [offset, setOffset] = React.useState(0);
  const [searchKeyword, setSearchKeyword] = React.useState("");
  const [isSearchShown, setIsSearchShown] = React.useState(false);
  const [openUserDetail, setOpenUserDetail] = React.useState(false);
  const [selectedParticipant, setSelectedParticipant] = React.useState();

  const spotlightPeers = useSelector(
    (state) => makeSpotlightPeersSelector(state)(state),
    shallowEqual
  );
  const passivePeers = useSelector(
    (state) => makePassivePeersSelector(state)(state),
    shallowEqual
  );

  const selectedPeerId = useSelector((state) => state.room.selectedPeerId);
  const isModerator = useSelector((state) =>
    state.me.roles.some((role) =>
      state.room.permissionsFromRoles.MODERATE_ROOM.includes(role)
    )
  );

  useEffect(() => {
    if (passivePeers.length <= defaultPeerCountOnPage) handleGoFirst();
  }, [passivePeers]);

  const defaultPeerCountOnPage = 10;

  const handleNext = () => {
    setOffset(offset + defaultPeerCountOnPage);
    setLimit(limit + defaultPeerCountOnPage);
  };

  const handlePrev = () => {
    setOffset(offset - defaultPeerCountOnPage);
    setLimit(limit - defaultPeerCountOnPage);
  };

  const handleGoFirst = () => {
    setOffset(0);
    setLimit(defaultPeerCountOnPage);
  };

  const handleSearchBarChange = (event) => {
    setOffset(0);
    setSearchKeyword(event.target.value);
  };

  const handleSearchDisable = () => {
    setIsSearchShown(!isSearchShown);
  };

  return (
    <React.Fragment>
      <ListMe />

      <div className={classes.divider} />

      <List
        dense
        subheader={
          <ListSubheader component="div" className={classes.listHeader}>
            <Typography variant="subtitle2" className={classes.listHeaderText}>
              <FormattedMessage
                id="room.spotlights"
                defaultMessage="Participants in Spotlight ({count})"
                values={{
                  count: spotlightPeers.length,
                }}
              />
            </Typography>
            <MoreActionsMenu />
          </ListSubheader>
        }
      >
        {spotlightPeers.map((peerId) => {
          return (
            <PeerListItem
              key={peerId}
              id={peerId}
              isModerator={isModerator}
              isSelected={peerId === selectedPeerId}
              onClick={() => {
                if (peerId.length > 8) {
                  setSelectedParticipant(peerId);
                  setOpenUserDetail(true);
                }
              }}
            />
          );
        })}
      </List>

      <div className={classes.divider} />

      <List
        dense
        subheader={
          <ListSubheader component="div" className={classes.listHeader}>
            <Typography variant="subtitle2" className={classes.listHeaderText}>
              <FormattedMessage
                id="room.passive"
                defaultMessage="Passive Participants ({count})"
                values={{
                  count: passivePeers.length,
                }}
              />
            </Typography>
            <IconButton
              style={{ float: "right" }}
              onClick={handleSearchDisable}
            >
              <SearchIcon style={{ fill: "#3289ef" }} />
            </IconButton>
          </ListSubheader>
        }
      >
        {isSearchShown && (
          <TextField
            id="search"
            className={classes.textInput}
            label={intl.formatMessage({
              id: "room.searchKeyword",
              defaultMessage: "Search Parameter",
            })}
            color="primary"
            value={searchKeyword}
            variant="standard"
            margin="normal"
            InputProps={{
              disableUnderline: true,
            }}
            onChange={handleSearchBarChange}
            fullWidth
          />
        )}

        {passivePeers
          .filter((p) =>
            p.displayName.toLowerCase().includes(searchKeyword.toLowerCase())
          )
          .slice(offset, limit)
          .map((peer) => {
            return (
              <PeerListItem
                key={peer.id}
                id={peer.id}
                s
                isModerator={isModerator}
                isSelected={peer.id === selectedPeerId}
                onClick={() => {
                  if (peer.id.length > 8) {
                    setSelectedParticipant(peer.id);
                    setOpenUserDetail(true);
                  }
                }}
              />
            );
          })}

        {passivePeers.length > defaultPeerCountOnPage && searchKeyword === "" && (
          <div className={classes.center}>
            <div style={{ marginLeft: "10px" }}>
              <IconButton
                onClick={handleGoFirst}
                className={classes.iconButton}
                size="small"
              >
                <SkipPreviousIcon />
              </IconButton>
              <IconButton
                onClick={handlePrev}
                disabled={offset <= 0 ? true : false}
                className={classes.iconButton}
                size="small"
              >
                <NavigateBeforeIcon />
              </IconButton>
              <IconButton
                onClick={handleNext}
                disabled={
                  offset + defaultPeerCountOnPage >= passivePeers.length
                    ? true
                    : false
                }
                className={classes.iconButton}
                size="small"
              >
                <NavigateNextIcon />
              </IconButton>
            </div>
          </div>
        )}
      </List>

      {openUserDetail && (
        <ParticipantDetail
          userId={selectedParticipant}
          onClose={() => {
            setOpenUserDetail(false);
          }}
        />
      )}
    </React.Fragment>
  );
};

export default ParticipantList;
